import { cva } from 'class-variance-authority';

// NOTE: touch-device pressed appearance is handled by the `pressScale` function
export const buttonVariants = cva(
  [
    // FIXME: switch back to "transition-colors" when `cantaloupe` scale +
    // high-prominence variant is fixed
    'inline-flex items-center justify-center whitespace-nowrap outline-hidden transition-all select-none',
    'focus-visible:outline-focusRing',
    'data-disabled:text-disabled data-disabled:fill-disabled data-disabled:pointer-events-none',
    // indicate interaction unavailable while pending
    'pending:cursor-wait',
    '[a&]:cursor-pointer',
  ],
  {
    variants: {
      prominence: {
        high: [
          'text-brandOnFill bg-fillBrand',
          // 'hover:bg-fillBrandHover',
          // 'pressed:bg-fillBrand',
          // FIXME: use tokens when `cantaloupe` scale is fixed
          'hover:brightness-125',
          'pressed:brightness-100',
          'data-disabled:bg-fillDisabled',
          'pending:bg-fillDisabled',
        ],
        default: [
          'bg-fill text-emphasis',
          'hover:bg-fillHover',
          'pressed:bg-fillPress pressed:text-default',
          'data-disabled:bg-fillDisabled',
          'pending:bg-fillDisabled',
        ],
        low: [
          'text-emphasis font-medium',
          'hover:bg-surfaceHover',
          'pressed:bg-surfacePress pressed:text-default',
        ],
      },
      // only apply padding when the button contains a text element
      size: {
        large: [
          'rounded-base min-h-12 min-w-12 text-base font-semibold',
          'has-[[slot=text]]:px-5',
        ],
        medium: [
          'rounded-base min-h-10 min-w-10 text-sm font-semibold',
          'has-[[slot=text]]:px-3.5',
        ],
        small: [
          'rounded-small min-h-8 min-w-8 text-sm font-semibold',
          'has-[[slot=text]]:px-3',
        ],
        tiny: [
          'rounded-xsmall min-h-6 min-w-6 text-xs font-semibold',
          'has-[[slot=text]]:px-2',
        ],
      },
      staticColor: { white: 'text-white', black: 'text-black' },
      layer: { zero: '', one: '', two: '' },
    },
    compoundVariants: [
      {
        staticColor: 'white',
        prominence: 'high',
        className: [
          'bg-white/85 text-black',
          'hover:bg-white hover:text-black',
          'pressed:bg-white/85 pressed:text-black',
          'data-disabled:bg-white/40 data-disabled:text-black/60',
          'pending:bg-white/40 pending:text-black/60',
        ],
      },
      {
        staticColor: 'white',
        prominence: 'default',
        className: [
          'bg-white/15 text-white',
          'hover:bg-white/20 hover:text-white',
          'pressed:bg-white/15 pressed:text-white',
          'data-disabled:bg-white/10 data-disabled:text-white/40',
          'pending:bg-white/10 pending:text-white/40',
        ],
      },
      {
        staticColor: 'white',
        prominence: 'low',
        className: [
          'text-white',
          'hover:bg-white/10 hover:text-white',
          'pressed:bg-white/5 pressed:text-white',
          'data-disabled:text-white/40',
          'pending:text-white/40',
        ],
      },
      {
        staticColor: 'black',
        prominence: 'high',
        className: [
          'bg-black/85 text-white',
          'hover:bg-black',
          'pressed:bg-black/85',
          'data-disabled:bg-black/40 data-disabled:text-white/60',
          'pending:bg-black/40 pending:text-white/60',
        ],
      },
      {
        staticColor: 'black',
        prominence: 'default',
        className: [
          'bg-black/15 text-black',
          'hover:bg-black/20 hover:text-black',
          'pressed:bg-black/15 pressed:text-black',
          'data-disabled:bg-black/10 data-disabled:text-black/40',
          'pending:bg-black/10 pending:text-black/40',
        ],
      },
      {
        staticColor: 'black',
        prominence: 'low',
        className: [
          'text-black',
          'hover:bg-black/10 hover:text-black',
          'pressed:bg-black/5 pressed:text-black',
          'data-disabled:text-black/40',
          'pending:text-black/40',
        ],
      },

      // layers
      {
        layer: 'one',
        prominence: 'high',
        className: 'data-disabled:bg-fillDisabled pending:bg-fillDisabled',
      },
      {
        layer: 'one',
        prominence: 'low',
        className: 'hover:bg-surfaceHover pressed:bg-surfacePress',
      },
      {
        layer: 'two',
        prominence: 'high',
        className:
          'data-disabled:bg-fillTwoDisabled pending:bg-fillTwoDisabled',
      },
      {
        layer: 'two',
        prominence: 'default',
        className: [
          'bg-fillTwo hover:bg-fillTwoHover pressed:bg-fillTwoPress',
          'data-disabled:bg-fillTwoDisabled pending:bg-fillTwoDisabled',
        ],
      },
      {
        layer: 'two',
        prominence: 'low',
        className: 'hover:bg-surfaceTwoHover pressed:bg-surfaceTwoPress',
      },
    ],
    defaultVariants: {
      size: 'medium',
      prominence: 'default',
    },
  }
);

// Use padding on the text element, instead of gap on the parent, to avoid a
// bunch of conditional variant styles for handling: text-only, icon-only, icon
// composition. Since icons are larger than their companion label the space
// around text is perceived to be smaller, even when equal.
export const textSizeClasses = {
  large: 'px-1.5',
  medium: 'px-1.5',
  small: 'px-1',
  tiny: 'px-0.5',
} as const;
